import { Flex } from 'antd';
import { PrimaryCard } from '../../assets/styles/components/dashboard.styled';
import {
  AddCircle,
  DocumentNormal,
  Location,
  Mobile,
  Personalicard,
  ProfileUser,
  ReceiveOutline,
  Sms,
  User,
} from '../icons';
import { CardCta, Divider, Paragraph } from '../Ant';
import { useTranslation } from 'react-i18next';
import CardData from '../dashboard/cardData';
import { Courthouse } from '../icons/Courthouse';
import { IConflictSearch } from '../../interface/conflictSearch';
import { compact } from 'lodash';
import {
  displayType,
  getConflictSearchInformativeIcon,
} from '../../constants/conflictSearchUtility';
import { displayFullCountryName } from '../../constants/utilityFunctions';
import { useLanguage } from '../../context/LanguageContext';
import { ROUTES } from '../../constants/constants';
import { useNavigationStack } from '../../context/NavigationStackContext';
import { ICustomerType } from '../../constants/customer';
import { BorderColor, TagColor } from '../../interface/common';
import SkeletonEntityCard from '../common/skeleton/skeletonEntityCard';
import { useFetchUserParameters } from '../../hooks/useFetchUserParameters';
import { ParameterEntityType } from '../../interface/parameters';
import { ProductName } from '../../interface/system';
import { useSystemInfo } from '../../context/SystemInfoContext';
import { useRestrictedUserMatterPermissions } from '../../hooks/useRestrictedUserMatterPermissions';

const ConflictDetailCard = ({
  details,
  isLoading = false,
}: {
  details?: IConflictSearch;
  isLoading?: boolean;
}) => {
  const { t } = useTranslation();
  const { push } = useNavigationStack();
  const { language } = useLanguage();
  const { systemParams } = useSystemInfo();
  const { canCreateMatterRequests } = useRestrictedUserMatterPermissions();
  const { data: parametersDetails } = useFetchUserParameters({
    parametersToGet: ParameterEntityType.FEATURES_IN_USE,
  });
  const showMatterRequests =
    parametersDetails?.parameters?.featuresInUseParameters?.matterRequestInUse &&
    systemParams?.systemInfo?.productName !== ProductName.LAKIMIES;

  const fullPrimaryAddress = compact([
    details?.primaryAddress?.line1,
    details?.primaryAddress?.line2,
    details?.primaryAddress?.line3,
    details?.primaryAddress?.city,
  ]).join(', ');
  const handleCreateConflictCheck = () => {
    push(ROUTES.CONFLICT_CHECKS, {
      customerId: details?.id,
      type: details?.type,
    });
  };
  const handleCreateMatterRequest = () => {
    push(ROUTES.MATTER_REQUEST, {
      customerId: details?.id,
      type: details?.type,
    });
  };
  if (isLoading) {
    return (
      <SkeletonEntityCard
        rows={[1, 1, 1, 1, 2, 1, 1]}
        withSubcontentOnFirstRow
        className="py-10 px-10 h-full"
      />
    );
  }
  if (!details) {
    return null;
  }
  return (
    <PrimaryCard
      $isDetail
      className={`d-flex flex-col gap-10 h-full conflict-card ${details?.type === ICustomerType.COMPANY ? BorderColor.GREEN : BorderColor.BEIGE}`}
    >
      <Flex justify="space-between" align="center" className="w-full">
        <Flex gap={10} align="center">
          <Flex
            justify="center"
            align="center"
            data-testid="conflict-detail-card-type-chip"
            className={`status-chip ${details?.type === ICustomerType.COMPANY ? TagColor.GREEN : TagColor.BEIGE}`}
          >
            {displayType(details.type, t)}
          </Flex>

          {getConflictSearchInformativeIcon({
            t,
            hasActiveConflictChecks: details.hasActiveConflictChecks,
            hasMatterRequests: showMatterRequests && details.hasMatterRequests,
            isBlackListed: details.isBlackListed,
            hasOppositionRole: details.hasOppositionRole,
            hasCoPartyRole: details.hasCoPartyRole,
          })}
        </Flex>
      </Flex>
      <Divider />
      <Flex vertical gap={10} className="flex-grow">
        <CardData
          icon={<ProfileUser />}
          type="matter"
          title={t('customer:customer')}
          content={details?.name ?? ''}
          subcontent={displayFullCountryName(details?.homeCountry, language) ?? ''}
          contentclass="lineclamp-1"
        />
        <CardData
          icon={<Mobile />}
          type="phone"
          title={t('customer:phoneNumber')}
          content={details?.primaryPhone ?? '-'}
        />
        <CardData
          icon={<Sms />}
          type="email"
          title={t('customer:email')}
          content={
            details?.email ? (
              <a href={`mailto:${details.email}`} className="conflict-detail-email">
                {details.email}
              </a>
            ) : (
              '-'
            )
          }
        />
        <CardData
          type="parentCompany"
          icon={<Courthouse />}
          title={t('customer:parentCompany')}
          content={'-'}
        />

        <Flex justify="space-between">
          <CardData
            icon={<Personalicard />}
            type="businessId"
            title={t('customer:businessId')}
            content={details?.businessId ?? '-'}
          />
          <CardData
            type="vatNumber"
            icon={<DocumentNormal />}
            title={t('customer:vatNumber')}
            content={details?.vatNumber ?? '-'}
          />
        </Flex>
        <CardData
          icon={<Location />}
          title={t('customer:address')}
          type="address"
          content={fullPrimaryAddress?.trim()?.length > 0 ? fullPrimaryAddress : '-'}
          contentclass="lineclamp-2"
        />
        <Divider />
        <CardData
          icon={<User />}
          type="responsiblePerson"
          title={t('customer:responsibleUser')}
          content={details?.responsibleUser?.fullName ?? '-'}
        />
      </Flex>
      <Flex vertical gap={10} className="w-full">
        <Divider />
        <Flex gap={15}>
          <CardCta data-testid="create-conflict-check-button" onClick={handleCreateConflictCheck}>
            <AddCircle />
            <Paragraph $fontsize="10px" $color="description" $weight={400} $lineheight="130%">
              {t('conflict:createConflictCheck')}
            </Paragraph>
          </CardCta>

          {showMatterRequests && canCreateMatterRequests && (
            <CardCta data-testid="create-matter-request-button" onClick={handleCreateMatterRequest}>
              <ReceiveOutline />
              <Paragraph $fontsize="10px" $color="description" $weight={400} $lineheight="130%">
                {t('matterRequest:createMatterRequest')}
              </Paragraph>
            </CardCta>
          )}
        </Flex>
      </Flex>
    </PrimaryCard>
  );
};

export default ConflictDetailCard;
